/**
 * @copyright 2025 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'

import { FiPlus, FiGrid, FiEdit, FiCheck, FiX, FiMenu, FiSettings } from 'react-icons/fi'
import { RiLayoutMasonryLine } from 'react-icons/ri'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { PageMenu, MenuAction, Loader, CustomGridLayout, Toast } from '@/components/ui'
import { PageTitle, useTitleInset } from '@/components/ui/page-header/AppTitleBar'
import { useAuth, useDashboard, useDocumentTitle } from '@/hooks'
import useCompany from '@/hooks/useCompany'
import ErrorBoundary from '@/insights/common/ErrorBoundary'
import Insight from '@/insights/components/Insight'
import { UnavailableInsightCard } from '@/insights/components/UnavailableInsightCard'
import { getInsightDefinition, insights, InsightKey } from '@/insights/insights'
import type {
  CustomGridBreakpointKey,
  DashboardType,
  InsightCardWidth,
  InsightConfigurationType,
  InsightDefinition,
  InsightParametersType,
  InsightStatus,
} from '@/types/dashboard-types'

import { DashboardConfigPanel } from './components/config/DashboardConfigPanel'
import { useDashboardControls, ControlsForm } from './components/TopNavDashboardControls'

const DEFAULT_PANEL: DashboardType = {
  slug: 'default',
  title: 'Default Dashboard',
  description: 'Default dashboard layout',
  insights: [],
  isDefault: true,
  parameters: {},
}

const InsightWrapper = ({
  dashboard,
  configuration,
  editMode,
  urlOverrides,
  isFullscreen,
  onFullscreenChange,
}: {
  dashboard: DashboardType
  configuration: InsightConfigurationType
  aspectRatio?: number
  editMode?: boolean
  urlOverrides?: InsightParametersType
  isFullscreen?: boolean
  onFullscreenChange?: (fs: boolean) => void
}) => {
  const { isInsightAllowed } = useAuth()
  // The registry is keyed by slug; a stored slug may point at an insight that has since
  // been retired, so look it up without the InsightKey cast.
  const _definition = (insights as Record<string, (typeof insights)[InsightKey] | undefined>)[configuration.slug]
  type DataType = Exclude<Awaited<ReturnType<NonNullable<typeof _definition>['load']>>, InsightStatus>
  type ParamsType = Parameters<NonNullable<typeof _definition>['configuration']['isValid']>[0]
  const definition = _definition as InsightDefinition<ParamsType, DataType> | undefined

  // Merge URL overrides on top of dashboard parameters
  const effectiveDashboard = useMemo(() => {
    if (!urlOverrides || Object.keys(urlOverrides).length === 0) return dashboard
    return {
      ...dashboard,
      parameters: { ...dashboard.parameters, ...urlOverrides },
    }
  }, [dashboard, urlOverrides])

  if (!definition) {
    return (
      <UnavailableInsightCard
        id={configuration.id}
        title={configuration.title}
        reason="unregistered"
        editMode={editMode}
      />
    )
  }
  if (!isInsightAllowed(configuration.slug)) {
    return (
      <UnavailableInsightCard
        id={configuration.id}
        title={configuration.title}
        reason="not-allowed"
        editMode={editMode}
      />
    )
  }

  return (
    <ErrorBoundary>
      <Insight
        configuration={configuration}
        dashboard={effectiveDashboard}
        definition={definition}
        editMode={editMode}
        isFullscreen={isFullscreen}
        onFullscreenChange={onFullscreenChange}
      />
    </ErrorBoundary>
  )
}

/**
 * Individual dashboard view component
 * Shows the dashboard content and statistics for a specific dashboard layout
 */
const DashboardView: React.FC = () => {
  const navigate = useNavigate()
  const dashboardContext = useDashboard()
  const titleInset = useTitleInset()
  const { companies } = useCompany()
  const [searchParams, setSearchParams] = useSearchParams()
  const dashboardSlug = useParams().slug || 'default'
  console.log({ dashboardview: { dashboardSlug, context: dashboardContext } })
  const dashboard = dashboardContext?.configuration?.dashboards?.find((p) => p.slug === dashboardSlug) || {
    ...DEFAULT_PANEL,
  }
  useDocumentTitle(dashboard.title)

  // URL query parameter overrides (temporary, never saved)
  const urlOverrides = useMemo<InsightParametersType>(() => {
    const overrides: InsightParametersType = {}
    const urlCompanyId = searchParams.get('companyId')
    if (urlCompanyId) {
      // Validate against user's company list
      const isValid = companies.some((c) => String(c.id) === urlCompanyId)
      if (isValid) overrides.companyId = Number(urlCompanyId)
    }
    const urlAssetId = searchParams.get('assetId')
    if (urlAssetId) overrides.assetId = urlAssetId
    const urlDateRange = searchParams.get('dateRange')
    if (urlDateRange) overrides.date = urlDateRange
    const urlFrom = searchParams.get('from')
    if (urlFrom) overrides.from = urlFrom
    const urlTo = searchParams.get('to')
    if (urlTo) overrides.to = urlTo
    return overrides
  }, [searchParams, companies])

  // Fullscreen insight state from URL
  const fullscreenInsightId = searchParams.get('fs')
  const setFullscreenInsight = useCallback(
    (id: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (id) {
            next.set('fs', id)
          } else {
            next.delete('fs')
          }
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const [isConfigDashboardOpen, setIsConfigDashboardOpen] = useState<'open' | 'closed' | 'add'>('closed')
  const [panelKey, setPanelKey] = useState(0)
  const [isEditingLayout, setIsEditingLayout] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [pendingCards, setPendingCards] = useState<Array<{
    id: string
    element: React.ReactElement
    config: {
      position?: {
        lg?: { x: number; y: number; w: number; h: number }
        md?: { x: number; y: number; w: number; h: number }
        sm?: { x: number; y: number; w: number; h: number }
      }
    }
  }> | null>(null)
  const [showToast, setShowToast] = useState(false)
  const [hamburgerOpen, setHamburgerOpen] = useState(false)
  const [controlsExpanded, setControlsExpanded] = useState(false)
  const hamburgerRef = useRef<HTMLDivElement>(null)

  const insightConfigs: InsightConfigurationType[] = dashboard.insights
  const dashboards = dashboardContext?.configuration?.dashboards ?? []

  useEffect(() => {
    if (dashboardContext.isLoading) {
      return
    }
    if (!dashboards.length) {
      return
    }
    const found = dashboards.find((d) => d.slug === dashboardSlug || (dashboardSlug === 'default' && d.isDefault))
    if (found) {
      dashboardContext.setCurrentDashboardSlug(found.slug)
      if (found.slug !== dashboardSlug) {
        navigate(`/dashboards/${found.slug}`, { replace: true })
      }
      return
    }
    const fallback = dashboards.find((d) => d.isDefault) || dashboards[0]
    if (fallback) {
      navigate(`/dashboards/${fallback.slug}`, { replace: true })
    } else {
      navigate('/dashboards/new', { replace: true })
    }
  }, [dashboardSlug, dashboardContext.isLoading, dashboards, navigate])

  useEffect(() => {
    setPendingCards(null)
    setHasUnsavedChanges(false)
  }, [insightConfigs?.length])

  // Close hamburger on outside click
  useEffect(() => {
    if (!hamburgerOpen) return
    const handler = (e: MouseEvent) => {
      if (hamburgerRef.current && !hamburgerRef.current.contains(e.target as Node)) {
        setHamburgerOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [hamburgerOpen])

  const cards = useMemo(() => {
    const result = insightConfigs?.map((configuration) => ({
      id: configuration.id!,
      element: (
        <InsightWrapper
          dashboard={dashboard}
          configuration={configuration}
          editMode={isEditingLayout}
          urlOverrides={urlOverrides}
          isFullscreen={fullscreenInsightId === configuration.id}
          onFullscreenChange={(fs: boolean) => setFullscreenInsight(fs ? configuration.id! : null)}
        />
      ),
      config: {
        width: configuration.width as InsightCardWidth,
        height: getInsightDefinition(configuration.slug as InsightKey)?.defaultHeight,
        aspectRatio: configuration.aspectRatio,
        position: configuration.position,
      },
    }))

    return result || []
  }, [
    insightConfigs,
    insightConfigs?.length,
    dashboard,
    isEditingLayout,
    urlOverrides,
    fullscreenInsightId,
    setFullscreenInsight,
  ])

  const handleCardsReorder = useCallback(
    (
      reorderedCards: Array<{
        id: string
        element: React.ReactElement
        config: {
          position?: {
            lg?: { x: number; y: number; w: number; h: number }
            md?: { x: number; y: number; w: number; h: number }
            sm?: { x: number; y: number; w: number; h: number }
          }
        }
      }>
    ) => {
      setPendingCards(reorderedCards)
      setHasUnsavedChanges(true)
    },
    []
  )

  const getCurrentBreakpoint = (): CustomGridBreakpointKey => {
    const width = window.innerWidth
    if (width >= 1024) return 'lg'
    if (width >= 768) return 'md'
    return 'sm'
  }

  const [currentViewport, setCurrentViewport] = useState<CustomGridBreakpointKey>(getCurrentBreakpoint())

  useEffect(() => {
    const handleResize = () => {
      setCurrentViewport(getCurrentBreakpoint())
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const handleSaveCustomGridLayout = () => {
    if (pendingCards && dashboard.slug) {
      const updatedInsights = insightConfigs.map((insight) => {
        const pendingCard = pendingCards.find((card) => card.id === insight.id)
        if (pendingCard?.config.position) {
          return {
            ...insight,
            position: pendingCard.config.position,
          }
        }
        return insight
      })

      const updatedDashboard = { ...dashboard, insights: updatedInsights }

      const updatedDashboards = dashboardContext.configuration.dashboards.map((d) =>
        d.slug === dashboard.slug ? updatedDashboard : d
      )

      dashboardContext.reorderDashboards(updatedDashboards)

      setHasUnsavedChanges(false)
      setPendingCards(null)
    }
    setIsEditingLayout(false)
    setShowToast(true)
  }

  const buildMenuActions = (compact: boolean): MenuAction[] => [
    {
      id: 'configure',
      label: compact ? 'Configure' : 'Configure Dashboard',
      icon: <FiSettings />,
      onClick: () => {
        setPanelKey((prev) => prev + 1)
        setIsConfigDashboardOpen('open')
      },
    },

    ...(!isEditingLayout
      ? [
          {
            id: 'edit-layout',
            label: compact ? 'Layout' : 'Edit Layout',
            icon: <RiLayoutMasonryLine />,
            onClick: () => {
              setIsEditingLayout(true)
            },
          },
        ]
      : []),
    ...(isEditingLayout
      ? [
          {
            id: 'save-layout',
            label: 'Save',
            icon: <FiCheck />,
            disabled: !hasUnsavedChanges,
            onClick: handleSaveCustomGridLayout,
          },
          {
            id: 'cancel-edit',
            label: 'Cancel',
            icon: <FiX />,
            onClick: () => {
              setIsEditingLayout(false)
              setHasUnsavedChanges(false)
              setPendingCards(null)
            },
          },
        ]
      : []),
  ]

  const menuActions = buildMenuActions(false)
  const compactMenuActions = buildMenuActions(true)

  const title = dashboard.title
  const dashboardParams = dashboard.parameters || {}
  const hasParams = !!(dashboardParams.companyId || dashboardParams.assetId || dashboardParams.date)
  const showInNav = !!dashboardParams.showInNav

  // Dashboard controls hook (shared across all breakpoint layouts)
  const controls = useDashboardControls({
    parameters: dashboardParams,
    onSave: (params) => dashboardContext.updateDashboardParameters(params),
  })

  if (dashboardContext.isLoading) {
    return <Loader fullScreen />
  }

  const onClose = (insightId?: string) => {
    if (insightId) {
      // Scroll new insight into view and highlight
      setTimeout(() => {
        const el = document.getElementById(insightId.replace(/\//g, '-'))
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' })
          const border = el.style.border
          Object.assign(el.style, { transition: 'border-color 2s', border: '2px solid yellow' })
          setTimeout(() => (el.style.border = border), 2000)
        }
      }, 400)
    }
    setIsConfigDashboardOpen('closed')
  }

  return (
    <div className="p-4 space-y-6">
      {/* Unified dashboard header — same layout regardless of showInNav, edit icon hidden when off */}
      <div
        className={`py-1 pr-4 ${titleInset} rounded-box shadow-md mb-4 relative z-20 bg-primary text-primary-content`}
      >
        <div className="flex items-center justify-between gap-2 min-h-8">
          <PageTitle className="truncate">{title}</PageTitle>
          <div className="flex items-center gap-2 flex-shrink-0">
            {hasParams && (
              <span className="text-sm text-primary-content/80 truncate max-w-[300px] hidden lg:inline">
                {controls.summaryText}
              </span>
            )}
            {showInNav && (
              <button
                onClick={() => setControlsExpanded(!controlsExpanded)}
                className="btn btn-sm btn-ghost btn-square text-primary-content"
                title="Edit parameters"
              >
                {controlsExpanded ? <FiX className="w-4 h-4" /> : <FiEdit className="w-4 h-4" />}
              </button>
            )}
            <div className="w-px h-6 bg-primary-content/20" />
            <div className="hidden xl:block">
              <PageMenu actions={menuActions} />
            </div>
            <div className="hidden md:block xl:hidden">
              <PageMenu actions={compactMenuActions} />
            </div>
            <div className="md:hidden relative" ref={hamburgerRef}>
              <button
                onClick={() => setHamburgerOpen(!hamburgerOpen)}
                className="btn btn-sm btn-ghost btn-square text-primary-content"
              >
                {hamburgerOpen ? <FiX className="w-5 h-5" /> : <FiMenu className="w-5 h-5" />}
              </button>
              {hamburgerOpen && (
                <div className="absolute top-full right-0 mt-2 bg-base-100 border border-base-300 rounded-lg shadow-xl p-4 z-50 w-64 text-base-content flex flex-col gap-2">
                  {menuActions.map((action) => (
                    <button
                      key={action.id}
                      className="btn btn-sm btn-outline justify-start"
                      onClick={() => {
                        action.onClick()
                        setHamburgerOpen(false)
                      }}
                      disabled={action.disabled}
                    >
                      {action.icon && <span className="mr-1">{action.icon}</span>}
                      {action.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        {hasParams && (
          <div className="lg:hidden mt-2">
            <span className="text-sm text-primary-content/80 truncate block">{controls.summaryText}</span>
          </div>
        )}
        {controlsExpanded && showInNav && (
          <div className="mt-3">
            <div className="hidden md:flex items-end gap-3">
              <div className="flex-1">
                <ControlsForm controls={controls} layout="horizontal" hideApply />
              </div>
              <button
                onClick={() => {
                  controls.handleSave()
                  setControlsExpanded(false)
                }}
                className={`btn btn-sm btn-ghost text-primary-content border-primary-content/30 hover:bg-primary-content/10 ${controls.hasChanges ? '' : 'btn-disabled'}`}
                disabled={!controls.hasChanges}
              >
                Save
              </button>
            </div>
            <div className="md:hidden">
              <ControlsForm controls={controls} layout="vertical" />
            </div>
          </div>
        )}
      </div>

      {isConfigDashboardOpen && (
        <div>
          <DashboardConfigPanel isOpen={isConfigDashboardOpen} onClose={onClose} key={panelKey} />
        </div>
      )}
      <div className="flex flex-wrap gap-3 mt-5">
        <CustomGridLayout
          key={`${dashboard.slug}`}
          cards={cards}
          onLayoutChange={handleCardsReorder}
          onUnsavedChangesChange={setHasUnsavedChanges}
          defaultBreakpoint={currentViewport}
          isEditMode={isEditingLayout}
        />
      </div>
      {/* Empty State */}
      {!dashboardContext.currentDashboard?.insights?.length && (
        <div className="text-center py-12">
          <FiGrid className="mx-auto w-16 h-16 text-base-content/30 mb-4" />
          <h3 className="text-xl font-semibold mb-2">No Insights Added</h3>
          <p className="text-base-content/60 mb-6">Add Insights to customize your dashboard view</p>
          <button onClick={() => setIsConfigDashboardOpen('add')} className="btn btn-primary">
            <FiPlus className="w-4 h-4 mr-2" />
            Add Your First Insight
          </button>
        </div>
      )}
      <Toast
        show={showToast}
        header="Layout Saved"
        body="Your custom dashboard layout has been saved"
        success
        position="end"
        onClose={() => setShowToast(false)}
      />
    </div>
  )
}

export default DashboardView
